// @ai-generated 2026-08-27T10:06:00+00:00 by malte.ulfers@tngtech.com using Claude Opus 5
import { COMPARE_OPERATOR_LABEL, UNARY_OPERATOR_LABEL } from '../fieldMetadata';
import type { SynqoCondition } from '@Model';
import styles from './InlinePredicate.module.css';
import { OperandRenderer } from './OperandRenderer';

interface InlinePredicateProps {
    node: SynqoCondition;
}

/**
 * Renders a *simple predicate* — compare/predicate, optionally combined with and/or — as one inline
 * phrase, without the card chrome `ConditionVisitor` produces.
 *
 * Needed because an aggregate operand renders inside a single flex row (see `CompareRenderer`), where
 * a nested card would break the layout. The backend restricts a filter to exactly this subset
 * (`CollectionFilter.RequireSimplePredicate`), which is what makes an inline rendering sufficient:
 * every allowed filter is a short boolean phrase over leaf tests.
 *
 * Leaf operands go back through `OperandRenderer`, so literals, fields and ambient values are styled
 * identically here and inside a full condition card.
 */
export function InlinePredicate({ node }: InlinePredicateProps) {
    switch (node.type) {
        case 'compare':
            return (
                <span className={styles.phrase}>
                    <OperandRenderer operand={node.left} />
                    <span className={styles.operator}>{COMPARE_OPERATOR_LABEL[node.operator]}</span>
                    <OperandRenderer operand={node.right} />
                </span>
            );

        case 'predicate':
            return (
                <span className={styles.phrase}>
                    <OperandRenderer operand={node.operand} />
                    <span className={styles.operator}>{UNARY_OPERATOR_LABEL[node.operator]}</span>
                </span>
            );

        case 'and':
        case 'or':
            return <InlineJunction type={node.type} conditions={node.children} />;

        default:
            // Unreachable for a filter, which the backend restricts to the cases above. Kept as a
            // visible marker rather than a silent null: the client cannot enforce that contract, so a
            // future backend change that relaxes it should be obvious here instead of rendering blank.
            return <span className={styles.unsupported}>Filter zu komplex für die Inline-Darstellung</span>;
    }
}

interface InlineJunctionProps {
    type: 'and' | 'or';
    conditions: readonly SynqoCondition[];
}

function InlineJunction({ type, conditions }: InlineJunctionProps) {
    const separator = type === 'and' ? 'und' : 'oder';

    return (
        <span className={styles.phrase}>
            {conditions.map((child, index) => (
                // Index key: AST nodes carry no id, and the tree is re-rendered wholesale from an
                // immutable ruleset, so position is a stable identity here.
                <span key={index} className={styles.phrase}>
                    {index > 0 && <span className={styles.junction}>{separator}</span>}
                    {/* Parenthesise a nested junction so "a und (b oder c)" cannot be misread. */}
                    {child.type === 'and' || child.type === 'or' ? (
                        <span className={styles.phrase}>
                            <span className={styles.paren}>(</span>
                            <InlinePredicate node={child} />
                            <span className={styles.paren}>)</span>
                        </span>
                    ) : (
                        <InlinePredicate node={child} />
                    )}
                </span>
            ))}
        </span>
    );
}
